import { useEffect, useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import { Icon } from 'semantic-ui-react';
import WorksheetForm from '~/features/projectWizard/worksheets/common/components/WorksheetForm';
import { WorksheetField } from '../../../worksheets/common/components/Field';
import { InputType } from '~/schema';
import { ExpensesUnit, UnitDecimals } from '~/constants';
import { useFadeInStyles, useProjectContext } from '~/hooks';
import { Button } from '~/components';
import Modal from '~/components/Modal';
import { formatNumber } from '~/utils';
import '../System/collapsibleCard.scss';

const defaultAdditionalExpense = {
  name: '',
  amount: null,
  escalator: null,
  expectedTerm: null,
  unit: ExpensesUnit.KWDC,
  notes: '',
  startOpen: true,
};

type AdditionalExpense = {
  name?: string;
  amount?: number | null;
  unit?: string;
  startOpen?: boolean;
};

function expenseHeaderValues(expense: AdditionalExpense) {
  const parts: string[] = [];
  if (expense.amount != null) {
    const isPerYear = expense.unit === ExpensesUnit.YEAR;
    const unitLabel = isPerYear ? 'per year' : 'per kWdc';
    const formatted = isPerYear
      ? formatNumber(expense.amount)
      : formatNumber(expense.amount, 0, { maxDec: 3 });
    parts.push(`$${formatted} ${unitLabel}`);
  }
  return parts.join(', ');
}

function ExpenseSection({
  expense,
  index,
  total,
  onRemove,
}: {
  expense: AdditionalExpense;
  index: number;
  total: number;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(total > 1 ? false : true);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  useEffect(() => {
    if (expense.startOpen) {
      setOpen(true);
      delete expense.startOpen;
    }
  }, []);

  const headerValues = expenseHeaderValues(expense);
  const title = expense.name?.trim()
    ? expense.name
    : `Expense ${index + 1}`;

  return (
    <section className="collapsible-card" style={useFadeInStyles()}>
      <Collapsible.Root open={open} onOpenChange={() => setOpen(!open)}>
        <div className="collapsible-card__header">
          <Collapsible.Trigger
            type="button"
            aria-label={open ? 'Collapse expense' : 'Expand expense'}
            className="collapsible-card__trigger"
          >
            <span className="collapsible-card__caret">
              <Icon name={open ? 'caret down' : 'caret right'} fitted />
            </span>
            <h3 className="collapsible-card__title">
              {title}{headerValues ? ` - ${headerValues}` : ''}
            </h3>
          </Collapsible.Trigger>
          <div className="collapsible-card__controls">
            <button
              type="button"
              aria-label="Remove expense"
              className="collapsible-card__remove"
              onClick={() => setShowRemoveConfirm(true)}
            >
              <Icon name="trash alternate outline" />
            </button>
          </div>
        </div>
        <Collapsible.Content className="CollapsibleContent">
          <WorksheetForm isInner>
            <WorksheetField
              fieldLabel="Name"
              placeholder="Enter name"
              schemaKey={`additionalOperatingExpenses.${index}.name`}
            />
            <WorksheetField
              fieldLabel="Expected annual cost"
              type="number"
              schemaKey={`additionalOperatingExpenses.${index}.amount`}
              numberType={InputType.DOLLARS}
              unitField="unit"
              units={[
                { value: ExpensesUnit.KWDC, label: 'per kWdc', decimals: UnitDecimals.KWDC },
                { value: ExpensesUnit.YEAR, label: 'per year', decimals: UnitDecimals.YEAR },
              ]}
            />
            <WorksheetField
              fieldLabel="Expected annual escalator"
              schemaKey={`additionalOperatingExpenses.${index}.escalator`}
              type="number"
              numberType={InputType.PERCENT}
              decimals={2}
              width="half"
            />
            <WorksheetField
              fieldLabel="Expected term"
              schemaKey={`additionalOperatingExpenses.${index}.expectedTerm`}
              type="number"
              decimals={0}
              label="years"
              width="half"
            />
            <WorksheetField
              fieldLabel="Notes"
              placeholder="Please describe the costs provided."
              schemaKey={`additionalOperatingExpenses.${index}.notes`}
              type="textarea"
            />
          </WorksheetForm>
        </Collapsible.Content>
      </Collapsible.Root>
      {showRemoveConfirm && (
        <Modal closeModal={() => setShowRemoveConfirm(false)}>
          <Modal.Title>Remove expense {index + 1}?</Modal.Title>
          <Modal.Content>
            <p>
              This will permanently remove this additional operating expense from the project.
              This action cannot be undone.
            </p>
          </Modal.Content>
          <Modal.Footer>
            <Button primary outlined onClick={() => setShowRemoveConfirm(false)}>
              Cancel
            </Button>
            <Button primary onClick={() => { onRemove(); setShowRemoveConfirm(false); }}>
              Remove expense
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </section>
  );
}

export default function OperatingExpenses() {
  const { project, updateProjectValue } = useProjectContext();

  const expenses: AdditionalExpense[] = project.additionalOperatingExpenses ?? [];

  const add = () => {
    updateProjectValue('additionalOperatingExpenses', [
      ...expenses,
      { ...defaultAdditionalExpense },
    ]);
  };

  const remove = (i: number) => {
    updateProjectValue('additionalOperatingExpenses', [
      ...expenses.slice(0, i),
      ...expenses.slice(i + 1),
    ]);
  };

  const hasExpenses = expenses.length > 0;

  return (
    <WorksheetForm>
      {hasExpenses ? (
        <>
          {expenses.map((expense, i) => (
            <ExpenseSection
              key={i}
              expense={expense}
              index={i}
              total={expenses.length}
              onRemove={() => remove(i)}
            />
          ))}
          <div className="collapsible-card-list-actions">
            <Button variant="link" onClick={add}>
              + Add another expense
            </Button>
          </div>
        </>
      ) : (
        <div className="collapsible-card-empty">
          <p>No additional operating expenses yet</p>
          <Button variant="link" onClick={add}>
            + Add expense
          </Button>
        </div>
      )}
    </WorksheetForm>
  );
}
